import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Trash2, Key, Shield, Info, Database, FileText } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const SecuredAssetsTermsPage = () => {
    const [loading, setLoading] = useState(true);

    // Terms Settings State
    const [termsSettings, setTermsSettings] = useState({
        section_tag: 'Know Your Terms',
        section_title: 'What Borrowers Should Know',
        rights_title: 'Your Rights',
        responsibilities_title: 'Your Responsibilities'
    });

    // Terms Items State
    const [termsItems, setTermsItems] = useState([]);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [itemModalData, setItemModalData] = useState({
        category: 'right',
        icon_name: 'Home',
        item_text: '',
        display_order: 0,
        is_active: true
    });
    const [editingItem, setEditingItem] = useState(null);
    const [itemDeleteConfirm, setItemDeleteConfirm] = useState({ isOpen: false, id: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, itemsRes] = await Promise.all([
                axios.get('/api/admin/secured-assets/terms-settings'),
                axios.get('/api/admin/secured-assets/terms-items')
            ]);

            if (settingsRes.data.success && settingsRes.data.data) {
                setTermsSettings(settingsRes.data.data);
            }
            if (itemsRes.data.success) {
                setTermsItems(itemsRes.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setTermsSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleSettingsSubmit = async () => {
        try {
            await axios.put('/api/admin/secured-assets/terms-settings', termsSettings);
            toast.success('Terms settings updated');
            fetchData();
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        }
    };

    const handleAddItem = (category) => {
        setEditingItem(null);
        setItemModalData({
            category: category,
            icon_name: category === 'right' ? 'Key' : 'Shield',
            item_text: '',
            display_order: (termsItems.filter(i => i.category === category).length + 1),
            is_active: true
        });
        setIsItemModalOpen(true);
    };

    const handleEditItem = (item) => {
        setEditingItem(item);
        setItemModalData({
            ...item,
            is_active: Boolean(item.is_active)
        });
        setIsItemModalOpen(true);
    };

    const handleItemSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingItem) {
                await axios.put(`/api/admin/secured-assets/terms-items/${editingItem.id}`, itemModalData);
                toast.success('Item updated');
            } else {
                await axios.post('/api/admin/secured-assets/terms-items', itemModalData);
                toast.success('Item created');
            }
            setIsItemModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving item:', error);
            toast.error('Failed to save item');
        }
    };

    const confirmDeleteItem = async () => {
        try {
            await axios.delete(`/api/admin/secured-assets/terms-items/${itemDeleteConfirm.id}`);
            toast.success('Item deleted');
            setItemDeleteConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting item:', error);
            toast.error('Failed to delete item');
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Loading...</div>;

    const rights = termsItems.filter(i => i.category === 'right');
    const responsibilities = termsItems.filter(i => i.category === 'responsibility');

    return (
        <div className="max-w-7xl mx-auto pb-12">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Secured Assets - Know Your Terms</h1>
                    <p className="text-slate-500 mt-1">Manage borrower rights and responsibilities</p>
                </div>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">
                <div className="flex items-center gap-2 mb-6 text-primary-900 border-b border-slate-50 pb-4">
                    <Info size={20} className="font-bold" />
                    <h2 className="text-lg font-bold">Main Section Settings</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Tag</label>
                        <input
                            type="text"
                            name="section_tag"
                            value={termsSettings.section_tag || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                        <input
                            type="text"
                            name="section_title"
                            value={termsSettings.section_title || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Rights Card Title</label>
                        <input
                            type="text"
                            name="rights_title"
                            value={termsSettings.rights_title || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Responsibilities Card Title</label>
                        <input
                            type="text"
                            name="responsibilities_title"
                            value={termsSettings.responsibilities_title || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                </div>


                <div className="flex justify-end">
                    <button
                        onClick={handleSettingsSubmit}
                        className="px-6 py-2.5 bg-primary-900 text-white rounded-xl font-semibold hover:bg-primary-800 transition-all shadow-md"
                    >
                        Save Settings
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Rights Management */}
                <div>
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center gap-2 text-emerald-700">
                            <Key size={20} className="font-bold" />
                            <h2 className="text-xl font-bold">{termsSettings.rights_title}</h2>
                        </div>
                        <button
                            onClick={() => handleAddItem('right')}
                            className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-xl hover:bg-emerald-700 transition-colors shadow-md font-semibold"
                        >
                            <Plus size={18} /> Add Right
                        </button>
                    </div>

                    <div className="space-y-4">
                        {rights.map((item) => (
                            <div key={item.id} className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex items-start gap-4 group">
                                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 flex-shrink-0">
                                    <Key size={20} />
                                </div>
                                <div className="flex-grow">
                                    <p className="text-slate-800 font-medium leading-relaxed">{item.item_text}</p>
                                    <div className="flex items-center gap-3 mt-2">
                                        <span className="text-xs text-slate-400">Order: {item.display_order}</span>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${item.is_active ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                                            {item.is_active ? 'Active' : 'Hidden'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleEditItem(item)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"><Pencil size={16} /></button>
                                    <button onClick={() => setItemDeleteConfirm({ isOpen: true, id: item.id })} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Responsibilities Management */}
                <div>
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center gap-2 text-amber-700">
                            <Shield size={20} className="font-bold" />
                            <h2 className="text-xl font-bold">{termsSettings.responsibilities_title}</h2>
                        </div>
                        <button
                            onClick={() => handleAddItem('responsibility')}
                            className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-xl hover:bg-amber-700 transition-colors shadow-md font-semibold"
                        >
                            <Plus size={18} /> Add Responsibility
                        </button>
                    </div>

                    <div className="space-y-4">
                        {responsibilities.map((item) => (
                            <div key={item.id} className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex items-start gap-4 group">
                                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600 flex-shrink-0">
                                    <Shield size={20} />
                                </div>
                                <div className="flex-grow">
                                    <p className="text-slate-800 font-medium leading-relaxed">{item.item_text}</p>
                                    <div className="flex items-center gap-3 mt-2">
                                        <span className="text-xs text-slate-400">Order: {item.display_order}</span>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${item.is_active ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                                            {item.is_active ? 'Active' : 'Hidden'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleEditItem(item)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"><Pencil size={16} /></button>
                                    <button onClick={() => setItemDeleteConfirm({ isOpen: true, id: item.id })} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Item Modal */}
            {isItemModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 animate-in zoom-in duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-slate-800">{editingItem ? 'Edit Item' : `Add ${itemModalData.category === 'right' ? 'Right' : 'Responsibility'}`}</h2>
                            <button onClick={() => setIsItemModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <Plus className="rotate-45" size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleItemSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Item Text</label>
                                <textarea
                                    value={itemModalData.item_text}
                                    onChange={(e) => setItemModalData({ ...itemModalData, item_text: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none h-24 resize-none"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Icon (Lucide Name)</label>
                                    <input
                                        type="text"
                                        value={itemModalData.icon_name}
                                        onChange={(e) => setItemModalData({ ...itemModalData, icon_name: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="Key, Shield, Home, etc."
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Display Order</label>
                                    <input
                                        type="number"
                                        value={itemModalData.display_order}
                                        onChange={(e) => setItemModalData({ ...itemModalData, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        min="1"
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col">
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Status</label>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={itemModalData.is_active}
                                        onChange={(e) => setItemModalData({ ...itemModalData, is_active: e.target.checked })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                    <span className="ml-3 text-sm font-medium text-slate-700">{itemModalData.is_active ? 'Active' : 'Hidden'}</span>
                                </label>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                                <button type="button" onClick={() => setIsItemModalOpen(false)} className="px-6 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-semibold hover:bg-slate-50 transition-all">Cancel</button>
                                <button type="submit" className="px-6 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all shadow-lg">{editingItem ? 'Update Item' : 'Create Item'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={itemDeleteConfirm.isOpen}
                onClose={() => setItemDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDeleteItem}
                title="Delete Item"
                message="Are you sure you want to delete this term item? This action cannot be undone."
            />
        </div>
    );
};

export default SecuredAssetsTermsPage;
